import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'

/**
 * The dashboard "Work queue" view: open workflow issues that need someone to act
 * (approval-rule / availability-rule / exit-check failures parked on a record).
 * This is the platform's live "needs action" surface - the generic engine raises
 * a WorkflowIssue whenever a record can't proceed - served by the tenant-scoped
 * `/workflows/issues/` endpoint. (The old cross-resource "pending approvals"
 * inbox was the deprecated PendingTransition surface, dropped from the engine.)
 */
export interface WorkQueueIssue {
  uuid: string
  summary: string
  detail: string
  issue_kind: string
  status: string
  resource_type: string
  resource_id: string
  source_state_label: string | null
  created_at: string
  context?: {
    record_number?: string
    assigned_to_name?: string
    subject?: string
  }
}

export function useWorkQueue() {
  return useQuery<WorkQueueIssue[]>({
    queryKey: ['work-queue', 'open-issues'],
    queryFn: () =>
      api.get('/workflows/issues/', { params: { status: 'OPEN' } }).then((r) => {
        // The endpoint wraps its list in the APIResponse envelope ({result}); be
        // defensive about the exact shape so a wrapper change never blanks it.
        const body = r.data as
          | { result?: WorkQueueIssue[]; results?: WorkQueueIssue[] }
          | WorkQueueIssue[]
          | undefined
        if (Array.isArray(body)) return body
        return body?.result ?? body?.results ?? []
      }),
  })
}
